/**
 * onesignal.js — thin wrapper around OneSignal Web SDK v16.
 *
 * We load the script only when needed (property landing route) and never on
 * the marketing pages or dashboard, so bounce visitors don't trigger the
 * OneSignal prompt policies.
 *
 * Usage:
 *   import { initOneSignalForProperty, requestPushPermission } from "@/lib/onesignal";
 *   await initOneSignalForProperty("prop_xxx", "user_yyy");
 *   // later, from a user gesture (button click):
 *   await requestPushPermission("prop_xxx");
 */

const SDK_URL = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID
  || "7fe8f3c6-42b4-4824-b5b4-675f3e75048c"; // safe: public identifier

let scriptPromise = null;
let initialised = false;

const loadScript = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.OneSignal) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.defer = true;
    s.async = true;
    s.dataset.onesignal = "v16";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("OneSignal SDK load failed"));
    document.head.appendChild(s);
  });
  return scriptPromise;
};

/**
 * Initialise OneSignal for the given property. Safe to call multiple times.
 * On second+ calls it only updates the tags (property_id / host_id).
 */
export const initOneSignalForProperty = async (propertyId, hostId, language = "it") => {
  if (!APP_ID || !propertyId) return null;
  try {
    await loadScript();
  } catch (e) {
    console.warn("[OneSignal] SDK load failed:", e);
    return null;
  }

  window.OneSignalDeferred = window.OneSignalDeferred || [];

  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        if (!initialised) {
          await OneSignal.init({
            appId: APP_ID,
            allowLocalhostAsSecureOrigin: true,
            // Auto-prompt is DISABLED: we drive permission from our own UI.
            autoRegister: false,
            autoResubscribe: true,
            notifyButton: { enable: false },
          });
          initialised = true;
        }
        // Tag the current subscription so the backend can address it later.
        await OneSignal.User.addTags({
          property_id: String(propertyId),
          host_id: hostId ? String(hostId) : "",
          lang: String(language || "it"),
        });
        resolve(OneSignal);
      } catch (err) {
        console.warn("[OneSignal] init failed:", err);
        resolve(null);
      }
    });
  });
};

/** Fire the native permission prompt. Must be called from a user gesture.
 *
 * @param {string|null} propertyId  When set, registers to
 *   `/api/property/{id}/push/register` (guest → property owner).
 * @param {object} [opts]
 * @param {string} [opts.registerEndpoint]  Full path (starts with `/api/…`)
 *   to POST the OneSignal id to. Overrides the property-based path.
 * @param {string} [opts.authToken]  Bearer token (only for admin endpoints).
 */
export const requestPushPermission = async (propertyId, opts = {}) => {
  if (typeof window === "undefined" || !window.OneSignal) return { granted: false };
  try {
    await window.OneSignal.Notifications.requestPermission();
    const permission = window.OneSignal.Notifications.permission;  // boolean
    if (permission) {
      const osId = window.OneSignal.User?.onesignalId;
      const path = opts.registerEndpoint
        || (propertyId ? `/api/property/${propertyId}/push/register` : null);
      if (osId && path) {
        try {
          const headers = { "Content-Type": "application/json" };
          if (opts.authToken) headers.Authorization = `Bearer ${opts.authToken}`;
          await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
            method: "POST",
            headers,
            body: JSON.stringify({ onesignal_id: osId, language: (navigator.language || "it").slice(0, 2) }),
          });
        } catch (e) {
          console.warn("[OneSignal] register call failed:", e);
        }
      }
    }
    return { granted: !!permission };
  } catch (e) {
    console.warn("[OneSignal] permission request failed:", e);
    return { granted: false };
  }
};

/**
 * Initialise OneSignal for the HOST DASHBOARD (Admin PWA).
 * Tags the subscription with `hostland_host=1` + `user_id` so the platform
 * super-admin can broadcast to every host with a single OneSignal filter.
 * Safe to call multiple times — the SDK init runs at most once, subsequent
 * calls only refresh tags.
 */
export const initAdminAppOneSignal = async (user, authToken) => {
  if (!APP_ID || !user?.user_id) return null;
  try {
    await loadScript();
  } catch (e) {
    console.warn("[OneSignal] SDK load failed:", e);
    return null;
  }

  window.OneSignalDeferred = window.OneSignalDeferred || [];
  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        if (!initialised) {
          await OneSignal.init({
            appId: APP_ID,
            allowLocalhostAsSecureOrigin: true,
            autoRegister: false,
            autoResubscribe: true,
            notifyButton: { enable: false },
          });
          initialised = true;
        }
        await OneSignal.User.addTags({
          hostland_host: "1",
          user_id: String(user.user_id),
          role: String(user.role || "host"),
          lang: (navigator.language || "it").slice(0, 2),
        });
        // If push is already granted (returning host), sync the OneSignal ID
        // to the backend so the admin knows they can address this host.
        try {
          const perm = OneSignal.Notifications.permission;
          const osId = OneSignal.User?.onesignalId;
          if (perm && osId && authToken) {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-app/push/register`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
              body: JSON.stringify({ onesignal_id: osId, language: (navigator.language || "it").slice(0, 2) }),
            });
          }
        } catch { /* silent */ }
        resolve(OneSignal);
      } catch (err) {
        console.warn("[OneSignal] admin init failed:", err);
        resolve(null);
      }
    });
  });
};

/** Current push permission state: "granted" | "denied" | "default" | "unsupported" */
export const getPushPermission = () => {
  if (typeof window === "undefined") return "unsupported";
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
};
